import React, { useEffect, useState } from 'react';
import { CalendarClock } from 'lucide-react';
import { useTheme } from '../../contexts/ThemeContext.tsx';
import {
  atualizarAgendamento,
  AgendamentoUpdate,
  AgendamentoResponse,
  parseApiError,
} from '../../services/api.ts';
import BaseModal from './_BaseModal.tsx';
import {
  extractDateTimeInputs,
  getAuth,
  nowDateInputs,
  toISOSimpleString,
} from './_helpers.ts';

interface RescheduleModalProps {
  isOpen: boolean;
  onClose: () => void;
  agendamentoId: number | null | undefined;
  agendamentoConsultaData?: string | null;
  /** true se o lead atualmente está em status no-show (faltou) — limpa flag ao reagendar */
  hadNoShow?: boolean;
  clinicId: number;
  onRescheduled?: (agendamento: AgendamentoResponse) => void;
}

const RescheduleModal: React.FC<RescheduleModalProps> = ({
  isOpen,
  onClose,
  agendamentoId,
  agendamentoConsultaData,
  hadNoShow,
  clinicId,
  onRescheduled,
}) => {
  const { isDark } = useTheme();
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [form, setForm] = useState({ date: '', time: '' });

  useEffect(() => {
    if (isOpen) {
      const { date, time } =
        extractDateTimeInputs(agendamentoConsultaData || undefined) || nowDateInputs();
      setForm({ date, time });
      setError(null);
    }
  }, [isOpen, agendamentoConsultaData]);

  const isMissingId = !agendamentoId;
  const canSubmit = !!agendamentoId && !!form.date && !!form.time;

  const handleSubmit = async () => {
    if (!agendamentoId || !canSubmit) return;
    setSaving(true);
    setError(null);
    try {
      const auth = getAuth(clinicId);
      const payload: AgendamentoUpdate = {
        consulta_data: toISOSimpleString(form.date, form.time),
        ...(hadNoShow ? { status: 'SCHEDULED' } : {}),
      };
      console.log('[RescheduleModal] Reagendando', { agendamentoId, payload });
      const updated = await atualizarAgendamento(
        auth.effectiveClientId,
        auth.clinicId,
        agendamentoId,
        payload
      );
      console.log('[RescheduleModal] Reagendado', updated);
      onRescheduled?.(updated);
      onClose();
    } catch (err: any) {
      console.error('[RescheduleModal] Erro:', err);
      const parsed = parseApiError(err, 'Não foi possível reagendar a consulta.');
      const alternatives = parsed.alternatives?.length
        ? ` Horários alternativos: ${parsed.alternatives.join(' ou ')}.`
        : '';
      setError(`${parsed.message}${alternatives}`);
    } finally {
      setSaving(false);
    }
  };

  return (
    <BaseModal
      isOpen={isOpen}
      onClose={onClose}
      title="Reagendar consulta"
      subtitle="Defina a nova data e hora"
      icon={CalendarClock}
      primaryLabel="Reagendar"
      onPrimary={isMissingId ? undefined : handleSubmit}
      primaryDisabled={!canSubmit}
      saving={saving}
      error={error}
    >
      {isMissingId ? (
        <p className={`text-sm ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
          Este contato não tem agendamento ativo para reagendar.
        </p>
      ) : (
        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-2">
            <label
              htmlFor="reschedule-date"
              className={`block text-sm font-medium ${isDark ? 'text-gray-300' : 'text-gray-700'}`}
            >
              Nova data
            </label>
            <input
              id="reschedule-date"
              type="date"
              value={form.date}
              onChange={(e) => setForm({ ...form, date: e.target.value })}
              className={`w-full p-3 border rounded-xl text-sm focus:ring-2 focus:ring-sminy-primary focus:border-sminy-primary transition-colors ${
                isDark ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300 text-gray-900'
              }`}
            />
          </div>
          <div className="space-y-2">
            <label
              htmlFor="reschedule-time"
              className={`block text-sm font-medium ${isDark ? 'text-gray-300' : 'text-gray-700'}`}
            >
              Nova hora
            </label>
            <input
              id="reschedule-time"
              type="time"
              value={form.time}
              onChange={(e) => setForm({ ...form, time: e.target.value })}
              className={`w-full p-3 border rounded-xl text-sm focus:ring-2 focus:ring-sminy-primary focus:border-sminy-primary transition-colors ${
                isDark ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300 text-gray-900'
              }`}
            />
          </div>
        </div>
      )}
    </BaseModal>
  );
};

export default RescheduleModal;
